import { useEffect, useMemo, useState, type ComponentProps } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, LoadingState, type DataTableColumn } from '../components/common';
import { useEventContext } from '../contexts/EventContext';
import { useRawDataQuery } from '../hooks/useRawDataQuery';
import { EVENT_LIST_ROUTE_PATH } from '../routes/appRoutes';
import type { RawDataItem, RawDataQueryParams } from '../services/rawDataApi';
import { timingPointService, type TimingPoint } from '../services/timingPointService';

const RAW_DATA_PAGE_SIZE = 20;

type RawDataFilters = {
  chipNumber: string;
  timingPointKeyword: string;
  from: string;
  to: string;
};

const emptyFilters: RawDataFilters = {
  chipNumber: '',
  timingPointKeyword: '',
  from: '',
  to: '',
};

function toQueryParams(filters: RawDataFilters): RawDataQueryParams {
  return {
    chipNumber: filters.chipNumber.trim() || undefined,
    timingPointKeyword: filters.timingPointKeyword.trim() || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
  };
}

function formatDateTime(value: string) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value.replace('T', ' ');
  }

  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function RawDataPage() {
  const { selectedEventId } = useEventContext();
  const [draftFilters, setDraftFilters] = useState<RawDataFilters>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<RawDataFilters>(emptyFilters);
  const [page, setPage] = useState(1);
  const [timingPoints, setTimingPoints] = useState<TimingPoint[]>([]);
  const queryParams = useMemo(() => ({
    ...toQueryParams(appliedFilters),
    page: page - 1,
    size: RAW_DATA_PAGE_SIZE,
  }), [appliedFilters, page]);
  const { data, error, isError, isFetching, isLoading, refetch } =
    useRawDataQuery(selectedEventId, queryParams);
  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(Math.ceil(totalCount / RAW_DATA_PAGE_SIZE), 1);
  const currentPage = Math.min(page, totalPages);
  const pageStartIndex = totalCount === 0 ? 0 : (currentPage - 1) * RAW_DATA_PAGE_SIZE + 1;
  const pageEndIndex = Math.min((currentPage - 1) * RAW_DATA_PAGE_SIZE + rows.length, totalCount);
  const columns = useMemo<DataTableColumn<RawDataItem>[]>(() => [
    { key: 'id', header: 'ID', sortable: true, width: 90 },
    { key: 'chipNumber', header: '칩 번호', sortable: true, width: 150 },
    {
      key: 'epcTime',
      header: '수신 시각',
      sortable: true,
      width: 210,
      render: (row) => formatDateTime(row.epcTime),
    },
    { key: 'timingPointName', header: '계측 지점', sortable: true, width: 180 },
  ], []);
  const errorMessage = error instanceof Error
    ? error.message
    : '원시 데이터를 불러오지 못했습니다.';

  useEffect(() => {
    if (selectedEventId == null) {
      setTimingPoints([]);
      setPage(1);
      return;
    }

    timingPointService
      .list(selectedEventId)
      .then(setTimingPoints)
      .catch(() => setTimingPoints([]));
  }, [selectedEventId]);

  useEffect(() => {
    if (!isFetching && page > totalPages) {
      setPage(totalPages);
    }
  }, [isFetching, page, totalPages]);

  const updateDraftFilter = (key: keyof RawDataFilters, value: string) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit: ComponentProps<'form'>['onSubmit'] = (event) => {
    event.preventDefault();

    const isSameQuery =
      page === 1 &&
      draftFilters.chipNumber === appliedFilters.chipNumber &&
      draftFilters.timingPointKeyword === appliedFilters.timingPointKeyword &&
      draftFilters.from === appliedFilters.from &&
      draftFilters.to === appliedFilters.to;

    setAppliedFilters(draftFilters);
    setPage(1);

    if (isSameQuery) {
      void refetch();
    }
  };

  const handleResetFilters = () => {
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(1);
  };

  const handlePreviousPage = () => {
    setPage((current) => Math.max(current - 1, 1));
  };

  const handleNextPage = () => {
    setPage((current) => Math.min(current + 1, totalPages));
  };

  let tableContent = (
    <section className="results-table-panel" aria-busy={isFetching}>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey="id"
        emptyMessage="표시할 원시 데이터가 없습니다."
      />
      <div className="results-pagination">
        <span>
          {totalCount === 0 ? '0건' : `${pageStartIndex}-${pageEndIndex} / ${totalCount}건`}
        </span>
        <div className="results-pagination-actions">
          <button type="button" onClick={handlePreviousPage} disabled={currentPage <= 1}>
            이전
          </button>
          <span>
            {currentPage} / {totalPages}
          </span>
          <button type="button" onClick={handleNextPage} disabled={currentPage >= totalPages}>
            다음
          </button>
        </div>
      </div>
    </section>
  );

  if (selectedEventId == null) {
    tableContent = (
      <div className="raw-data-message">
        선택된 대회가 없습니다. <Link to={EVENT_LIST_ROUTE_PATH}>대회 목록에서 선택</Link>해 주세요.
      </div>
    );
  } else if (isLoading) {
    tableContent = <LoadingState message="원시 데이터를 불러오는 중입니다." />;
  }

  return (
    <div className="raw-data-page">
      <div className="raw-data-title-row">
        <div className="page-title-block">
          <p className="page-section">Results</p>
          <h1>원시 데이터 조회</h1>
          <p className="page-description">리더기에서 수신된 원시 기록 데이터를 조회합니다.</p>
        </div>
        <button
          className="raw-data-refresh-button"
          type="button"
          disabled={selectedEventId == null || isFetching}
          onClick={() => {
            void refetch();
          }}
        >
          새로고침
        </button>
      </div>

      <form className="raw-data-filter-row" onSubmit={handleSubmit}>
        <input
          className="raw-data-filter-control"
          type="text"
          placeholder="칩 번호"
          value={draftFilters.chipNumber}
          onChange={(event) => updateDraftFilter('chipNumber', event.target.value)}
        />
        <select
          className="raw-data-filter-control"
          aria-label="계측 지점"
          value={draftFilters.timingPointKeyword}
          onChange={(event) => updateDraftFilter('timingPointKeyword', event.target.value)}
        >
          <option value="">계측 지점 전체</option>
          {timingPoints.map((timingPoint) => (
            <option key={timingPoint.id} value={timingPoint.timingPointName}>
              {timingPoint.timingPointName}
            </option>
          ))}
        </select>
        <label className="raw-data-time-field">
          <span>시작:</span>
          <input
            className="raw-data-filter-control raw-data-time-control"
            type="datetime-local"
            aria-label="시작 시각"
            value={draftFilters.from}
            onChange={(event) => updateDraftFilter('from', event.target.value)}
          />
        </label>
        <label className="raw-data-time-field">
          <span>종료:</span>
          <input
            className="raw-data-filter-control raw-data-time-control"
            type="datetime-local"
            aria-label="종료 시각"
            value={draftFilters.to}
            onChange={(event) => updateDraftFilter('to', event.target.value)}
          />
        </label>
        <button className="raw-data-search-button" type="submit" disabled={isFetching}>
          조회
        </button>
        <button
          className="raw-data-reset-button"
          type="button"
          disabled={isFetching}
          onClick={handleResetFilters}
        >
          초기화
        </button>
      </form>

      {isError && (
        <div className="raw-data-message raw-data-error" role="alert">
          {errorMessage}
        </div>
      )}

      {tableContent}
    </div>
  );
}

export default RawDataPage;
